<template>
  <b-row>
    <b-col cols="12" style="padding: 8px;">
      <strong>
        <span class="sidebar-title">Instruments</span>
      </strong>
      <i
        class="fas fa-info-circle"
        v-b-popover.hover.bottom="`Select desired instruments by selecting them below.
        This will filter infrastructures that contain those instruments`"
        title="Select Instruments"
      ></i>
      <b-row>
        <b-col cols="9" class="p-0">
          <span style="font-size: 0.8rem">Show Instruments on map</span>
        </b-col>
        <b-col cols="3" style="text-align: right !important;">
          <toggle-button v-model="toggleValue" :labels="true" />
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          class="p-0"
          style="padding-left: 10px; padding-right: 0px; height: 40rem; overflow-y: scroll;"
        >
          <b-form-checkbox
            v-for="ins in instrumentCounts"
            v-model="selectedInstrumentList"
            :key="ins.instrument_name"
            :value="ins.instrument_name"
            :id="ins.instrument_name"
            class="instrument-check"
          >
            <b-row>
              <b-col cols="10" class="p-0 text-left">
                <!--Inst icons in separate component -->
                <inst-icons :instAT="ins.asset_type"></inst-icons>
                <span>{{ins.instrument_name}}</span>
              </b-col>
              <b-col cols="2" class="p-0 pr-1 text-right">
                <b-badge variant="light">{{ins.count}}</b-badge>
              </b-col>
            </b-row>
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-modal
        :ref="ins.reference_designator"
        size="xl"
        scrollable
        title="Instrument Details"
        hide-footer
        v-for="ins in instruments"
        :key="`${ins.reference_designator}-modal`"
        lazy
      >
        <instrument-modal
          :refdes="ins.reference_designator"
          :instrument="ins"
          :parameters="[]"
        ></instrument-modal>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
/**
 * Instruments Component
 *
 * This component contains all the interactions for the
 * instruments section of the map sidebar.
 */
/* eslint-disable max-len */
/* eslint-disable vue/multi-word-component-names */
import _ from 'lodash';
import { mapState, mapActions, mapMutations } from 'vuex';
import InstrumentsModalVue from './InstrumentsModal';
import InstIconsVue from './instIcons';

export default {
  name: 'Instruments',
  data() {
    return {
      selected: [],
      instrument_names: [],
      toggleValue: true,
      lastSelectedInst: [],
      instrumentLayers: [
        'instrument-layers-point',
        'cluster-text',
        'instrument-layers-clusters',
      ],
    };
  },
  components: {
    'instrument-modal': InstrumentsModalVue,
    'inst-icons': InstIconsVue,
  },
  mounted() {
    this.setInstrumentModals({ refs: this.$refs });
    this.updateInstCheckbox([]);
  },
  methods: {
    ...mapActions([
      'setSelectedInstrument',
      'setInstrumentModals',
    ]),
    ...mapMutations([
      'updateInstCheckbox', /** map action, saves the inst checkbox to store */
    ]),
    addSelected(key) {
      this.instrument_names.push(key);
    },
  },
  computed: {
    ...mapState({
      instruments: (state) => state.instruments,
      infrastructures: (state) => state.infrastructures,
      webmap: (state) => state.webmap,
      infraCheckboxes: (state) => state.infraCheckboxSelection,
    }),
    /**
     * Sets up a getter and setting for the list of selected instruments
     */
    selectedInstrumentList: {
      get() {
        return this.$store.state.selectedInstrumentList;
      },
      set(value) {
        this.setSelectedInstrument({ selected: value });
      },
    },
    /**
     * Computed() - Calculates the counts for each unique instrument
     * and maps the count to the respective instrument object.
     * Sorted alphbetically by instrument name.
     */
    instrumentCounts() {
      const instrumentsObj = {};
      this.instruments.forEach((inst) => {
        let count = 0;
        if (_.has(instrumentsObj, inst.instrument_name)) {
          instrumentsObj[inst.instrument_name] += 1;
        } else {
          count += 1;
          instrumentsObj[inst.instrument_name] = count;
        }
      });
      const instrumentCountList = Object.keys(instrumentsObj).map((key) => {
        this.addSelected(key);
        const instrument = _.find(this.instruments, ['instrument_name', key]);
        return {
          ...instrument,
          count: instrumentsObj[key],
        };
      });
      return _.sortBy(instrumentCountList, 'instrument_name');
    },
    /**
     * Computed() - Maps the unique instruments names (not object)
     */
    instNames() {
      // Computed infrastructure names only (not object)
      // of the unique infrastructures
      return this.instrumentCounts.map((inst) => inst.instrument_name);
    },
  },
  watch: {
    /**
     * Watcher() - Used to update the value of the selected inst checkboxes
     * which will update the layout property of the map icons according to
     * the selected infrastructures.
     * @param {array} newVal - array of instruments checkboxes
     */
    selectedInstrumentList(newVal) {
      if (newVal.length > 0 || this.infraCheckboxes.length > 0) { // there are inst selected
        this.webmap.setLayoutProperty('infra-icon', 'visibility', 'none');
        this.webmap.setLayoutProperty('instrument-layers-point', 'visibility', 'none');
        this.webmap.setLayoutProperty('instrument-layers-clusters', 'visibility', 'none');
        this.webmap.setLayoutProperty('cluster-text', 'visibility', 'none');
        this.webmap.setLayoutProperty('mooring-circle', 'visibility', 'none');
        this.webmap.setLayoutProperty('mooring-text', 'visibility', 'none');
        this.updateInstCheckbox(newVal);
      } else if (newVal.length === 0 && this.infraCheckboxes.length === 0) {
        this.webmap.setLayoutProperty('infra-icon', 'visibility', 'visible');
        this.webmap.setLayoutProperty('instrument-layers-point', 'visibility', 'visible');
        this.webmap.setLayoutProperty('instrument-layers-clusters', 'visibility', 'visible');
        this.webmap.setLayoutProperty('cluster-text', 'visibility', 'visible');
        this.webmap.setLayoutProperty('mooring-circle', 'visibility', 'visible');
        this.webmap.setLayoutProperty('mooring-text', 'visibility', 'visible');
        this.updateInstCheckbox(newVal);
      }
    },
    /**
     * Watcher() - Used for the 'show all' instruments toggle button
     * on top of the infrastructure list.
     * @param {boolean} newVal - toggle value
     */
    toggleValue(newVal) {
      if (newVal === false) {
        this.instrumentLayers.forEach((layerID) => this.webmap.setLayoutProperty(layerID, 'visibility', 'none'));
      } else if (newVal === true) {
        this.instrumentLayers.forEach((layerID) => this.webmap.setLayoutProperty(layerID, 'visibility', 'visible'));
      }
    },
  },
};
</script>

<style>
.instrument-check label.custom-control-label {
  width: 100% !important;
}
</style>
